import React from 'react';
import { useOutletContext, Link } from "react-router-dom";
import { Box, Grid, List } from '@mui/material';
import { useSample, useClonalityComparisons } from '../services/api/KeynomeAPIHooks.js'

/**
 * A component that displays a list of clonal samples, where each row in the list is a link to 
 * the page for that sample.
 */
export default function ClonalSamplesList(props) {
  const { sample } = props;
  const { hasAccessClonality } = useOutletContext();
  const { data: clonalityResults } = useClonalityComparisons(sample?.id);

  // Clonality is currently only for internal use
  if (!hasAccessClonality) { return null }

  // Query for the clonality results for the sample
  if (!clonalityResults) {
    return null
  }
  const { data: clonalityComparisons, included: includedRecords } = clonalityResults;
  if (!clonalityComparisons || clonalityComparisons.length === 0) {
    return null
  }

  // Helper to extract records of a certain type from the "included" list in the query results,
  // forming a map from record ID to record.
  const extractFromIncluded = (record_type) => {
    return includedRecords.reduce((map, record) => {
      if (record.type === record_type) {
        map[record.id] = record;
      }
      return map;
    }, {})
  }
  const libSeqs = extractFromIncluded("lib_seq");
  const concatenatedFastqgzs = extractFromIncluded("concatenated_fastqgz") ;
  const sequencingRuns = extractFromIncluded("sequencing_run");

  // Construct a row component for each clonal sample
  const clonalSamplesListItems = clonalityComparisons.toSorted((left, right) => 
    right.attributes.distance_metric_value - left.attributes.distance_metric_value
  ).map((clonalityComparison) => {
    // Determine the ID of the other ConcatenatedFastqgz of the comparison
    let otherConcatenatedFastqgz = concatenatedFastqgzs[
      clonalityComparison.attributes.left_concatenated_fastqgz_id
    ];
    if (otherConcatenatedFastqgz === undefined) {
      otherConcatenatedFastqgz = concatenatedFastqgzs[
        clonalityComparison.attributes.right_concatenated_fastqgz_id
      ];
    }
    if (!otherConcatenatedFastqgz) {
      console.error(`Error: clonality results did not include ConcatenatedFastqgz record for comparison ${JSON.stringify(clonalityComparison)}!`);
      return null
    }
    const otherLibSeq = libSeqs[otherConcatenatedFastqgz.attributes.lib_seq_id];
    const otherSequencingRun = sequencingRuns[otherLibSeq.attributes.run_id];

    return (
      <ClonalSampleListItem 
        key={clonalityComparison.id}
        clonalityComparison={clonalityComparison}  
        concatenatedFastqgz={otherConcatenatedFastqgz}
        libSeq={otherLibSeq}
        sequencingRun={otherSequencingRun}
      />
    )
  })

  return (
    <>
      <Box sx={{ backgroundColor: '#eee', padding: '1px 10px' }}>
        <Grid container>
          <Grid item xs={12}>
            <p><b>POTENTIALLY RELATED SAMPLES</b></p>
          </Grid>
        </Grid>
      </Box>
      <Box sx={{ padding: '1px 10px', color: 'black' }}>
        <Grid container sx={{ pl: 1, pr: 1, textAlign: 'center' }}>
          <Grid item xs={12} sm={2} sx={{ textAlign: 'center' }}>
            <p><b>Sample Name</b></p>
          </Grid>
          <Grid item xs={12} sm={3} sx={{ textAlign: 'center' }}>
            <p>Relatedness</p>
          </Grid>
          <Grid item xs={12} sm={2} sx={{ textAlign: 'center' }}>
            <p>Score</p>
          </Grid>
          <Grid item xs={12} sm={3} sx={{ textAlign: 'center' }}>
            <p>Dataset</p>
          </Grid>
          <Grid item xs={12} sm={2} sx={{ textAlign: 'center' }}>
            <p>Processed On</p>
          </Grid>
        </Grid>
      </Box>
      <List sx={{
        width: '100%', 
        bgcolor: 'background.paper', 
        paddingTop: '0', 
        paddingBottom: '0', 
        maxHeight: "250px", 
        overflowY: 'auto'
      }}
      >
        {clonalSamplesListItems}
      </List>
    </>
  );
}

// Base styling for each item in a row
const listItemStyle = {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    margin: '10px 20px 5px 20px'
}

/**
 * Child component for each row in the clonal samples list. This row represents each clonal sample,
 * and it itself a link to that sample's page.
 */
function ClonalSampleListItem(props) {
  const { clonalityComparison, concatenatedFastqgz, libSeq, sequencingRun } = props;

  const { data: sample } = useSample(libSeq?.id);
  if (!sample) {
    return null
  }
  const sampleUrl = new URL(sample.attributes.keynome_web_url);

  const relatedness = determineRelatedness(clonalityComparison);
  if (!relatedness) {
    return null
  }

  return (
    <Link to={sampleUrl.pathname}>
      <Box component="li">
        <Grid
          container
          sx={{
            pl: 2, 
            pr: 1, 
            textAlign: 'center', 
            borderBottom: '#bdbdbd 2px solid', 
            '&:hover': { backgroundColor: '#f5f5f5' },
          }}
        >
          <Grid item xs={2} sx={{ textAlign: 'center' }}>
            <p style={listItemStyle}><b>{libSeq.attributes.sample_name}</b></p>
          </Grid>
          <Grid item xs={3} sx={{ textAlign: 'center' }}>
            <p style={listItemStyle}>{relatedness}</p>
          </Grid>
          <Grid item xs={2} sx={{ textAlign: 'center' }}>
            <p style={listItemStyle}>
              {parseFloat(clonalityComparison.attributes.distance_metric_value).toFixed(2)}
            </p>
          </Grid>
          <Grid item xs={3} sx={{ textAlign: 'center' }}>
            <p style={listItemStyle}>{sequencingRun.attributes.name}</p>
          </Grid>
          <Grid item xs={2} sx={{ textAlign: 'center' }}>
            <p style={listItemStyle}>
              <small>{new Date(concatenatedFastqgz.attributes.created_at).toLocaleString()}</small>
            </p>
          </Grid>
        </Grid>
      </Box>
    </Link>
  );
}

/**
 * Helper function to construct the "Relatedness" display element from a clonality Comparison
 */
function determineRelatedness(clonalityComparison) {
  if (clonalityComparison.attributes.comparison_method === "ksim") {
    if (clonalityComparison.attributes.distance_metric_value >= 0.95) {
      return (
        <span style={{ color: '#dc895d' }}><b>POTENTIALLY CLONAL</b></span>
      )
    } else if (clonalityComparison.attributes.distance_metric_value >=0.6) {
      return (
        <span style={{ color: '#4285a5' }}><b>DISTANTLY RELATED</b></span>
      )
    } else {
      return null
    }
  }

  return null
}
